<template>
  <div class="advertiser-overall-page">
    <b-row>
      <b-col lg="4" md="12" class="mb-2 mb-lg-0">
        <b-card class="h-100 left-card">
          <div class="text-center mb-1">
            <b-avatar size="96" :src="company.logo" class="company-logo" />
          </div>
          <small class="text-muted">Advertisers Details</small>

          <div v-if="isLoading" class="text-center py-2">
            <b-spinner small />
          </div>
          <b-alert v-else-if="loadError" show variant="danger" class="mt-1">
            {{ loadError }}
          </b-alert>
          <div v-else class="details mt-1">
            <p v-if="company.account_status">
              <strong>Account status:</strong>
              <b-badge :variant="statusBadgeVariant(company.account_status)" class="ml-50">
                {{ company.account_status }}
              </b-badge>
            </p>
            <p v-if="company.is_suspended">
              <strong>Suspension:</strong>
              <span class="text-danger">{{ company.remark || 'Suspended by admin' }}</span>
            </p>
            <p><strong>Company Name:</strong> {{ company.company_name || '—' }}</p>
            <p><strong>Email:</strong> {{ company.email || '—' }}</p>
            <p><strong>Address:</strong> {{ company.address || '—' }}</p>
            <p><strong>Zip code:</strong> {{ company.pincode || '—' }}</p>
            <p><strong>Country:</strong> {{ company.city || '—' }}</p>
            <p><strong>State of Incorporation:</strong> {{ company.state_of_incorporation || '—' }}</p>
            <p>
              <strong>Followers:</strong>
              <b-link class="followers-link" @click="$router.push({ name: 'advertiser-followers', params: { id: $route.params.id } })">{{ company.followers }}</b-link>
            </p>
            <p><strong>Running Ads:</strong> {{ company.running_ads }}</p>
            <p><strong>Contact Person Name:</strong> {{ company.contact_person_name || '—' }}</p>
            <p><strong>Contact:</strong> {{ company.contact || '—' }}</p>
            <p><strong>Reported Ads:</strong> {{ company.reported_users }}</p>
            <p><strong>Joining date:</strong> {{ company.joining_date || '—' }}</p>
          </div>

          <div v-if="!isLoading && !loadError" class="d-flex flex-wrap justify-content-between mt-1 action-row">
            <template v-if="company.is_suspended">
              <b-button variant="success" class="action-btn" :disabled="isSuspending" @click="unsuspendAdvertiser">
                Unsuspend
              </b-button>
            </template>
            <template v-else>
              <b-button variant="danger" class="suspend-btn" :disabled="isSuspending" @click="showTempSuspendModal = true">
                Temporary Suspend
              </b-button>
              <b-button variant="danger" class="suspend-btn" :disabled="isSuspending" @click="confirmPermanentSuspend">
                Suspend
              </b-button>
            </template>
          </div>
        </b-card>
      </b-col>

      <b-col lg="8" md="12">
        <b-card class="h-100 right-card">
          <b-tabs pills nav-class="mb-2 custom-tabs">
            <b-tab title="Overall Details" active>
              <div class="d-flex flex-wrap align-items-center justify-content-between mb-1 chart-head">
                <div class="d-flex align-items-center flex-wrap period-control-wrap">
                  <h4 class="mb-0 mr-2">Ads</h4>
                  <label class="text-muted mb-0 mr-50">Last</label>
                  <b-form-select
                    v-model="selectedPeriodDays"
                    :options="periodDaysOptions"
                    class="period-days-select"
                    @change="onPeriodDaysChange"
                  />
                  <label class="text-muted mb-0 ml-50">Day's</label>
                </div>
                <div class="d-flex flex-wrap align-items-center legend-wrap">
                  <span class="legend-item"><span class="dash dash-blue" />{{ legendStats.ads }} Ads</span>
                  <span class="legend-item"><span class="dash dash-red" />{{ legendStats.followers }} Followers</span>
                  <span class="legend-item"><span class="dash dash-black" />{{ legendStats.clicks }} Clicks</span>
                </div>
              </div>

              <vue-apex-charts v-if="!isLoadingChart" type="line" height="360" :options="chartOptions" :series="chartSeries" />
              <div v-else class="text-center py-3">
                <b-spinner label="Loading chart..." />
              </div>
            </b-tab>

            <b-tab title="Uploaded Ads" @click="fetchUploadedAds">
              <div class="d-flex justify-content-end mb-1 upload-controls">
                <b-form-input v-model="adsSearch" placeholder="Search ads..." class="search-input mr-1" @input="debouncedAdsSearch" />
              </div>

              <div v-if="isLoadingAds" class="text-center py-2">
                <b-spinner label="Loading ads..." />
              </div>
              <div v-else-if="!uploadedAds.length" class="text-center text-muted py-2">
                No uploaded ads found.
              </div>
              <b-row v-else>
                <b-col v-for="ad in uploadedAds" :key="`ad-${ad.id}`" lg="4" md="6" sm="12" class="mb-1">
                  <b-card no-body class="uploaded-card">
                    <div class="uploaded-image-wrap">
                      <b-img :src="ad.image" fluid class="uploaded-image" />
                      <b-badge pill :variant="adStatusVariant(ad.status)" class="status-chip">
                        {{ ad.statusLabel }}
                      </b-badge>
                    </div>
                    <b-card-body class="p-1">
                      <div class="d-flex justify-content-between align-items-center">
                        <h5 class="mb-0 text-white">{{ ad.title || 'Ad Title' }}</h5>
                        <small class="text-light">{{ ad.timeAgo || '' }}</small>
                      </div>
                      <small class="text-light d-block mt-50">{{ ad.statsLine }}</small>
                      <div class="d-flex justify-content-between align-items-center mt-50">
                        <small class="text-light">{{ ad.engagementLine }}</small>
                        <b-button
                          size="sm"
                          variant="danger"
                          class="trash-btn"
                          @click="confirmDeleteUploadedAd(ad)"
                        >
                          <feather-icon icon="Trash2Icon" size="13" />
                        </b-button>
                      </div>
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>

              <AdminPaginationBar
                :page="uploadedAdsPage"
                :per-page="uploadedAdsPerPage"
                :total="uploadedAdsTotal"
                @change="onUploadedAdsPageChange"
              />
            </b-tab>

            <b-tab title="Report" @click="fetchAdvertiserReports">
              <div class="d-flex justify-content-end mb-1 upload-controls">
                <b-form-input v-model="reportSearch" placeholder="Search reports..." class="search-input mr-1" @input="debouncedReportSearch" />
              </div>

              <div v-if="isLoadingReports" class="text-center py-2">
                <b-spinner label="Loading reports..." />
              </div>
              <div v-else-if="!reportRows.length" class="text-center text-muted py-2">
                No reports found for this advertiser.
              </div>
              <b-table v-else :items="reportRows" :fields="reportFields" responsive class="report-table" tbody-tr-class="report-row">
                <template #cell(reportedAd)="data">
                  <div class="d-flex align-items-center">
                    <b-avatar :src="data.item.reportedAdLogo" size="34" class="mr-50" />
                    <span>{{ data.item.reportedAdTitle }}</span>
                  </div>
                </template>
                <template #cell(reportedBy)="data">
                  <div class="d-flex align-items-center">
                    <b-avatar :src="data.item.reportedByAvatar" size="28" class="mr-50" />
                    <span>{{ data.item.reportedByName }}</span>
                  </div>
                </template>
                <template #cell(actions)="data">
                  <div class="d-flex align-items-center">
                    <feather-icon
                      icon="MailIcon"
                      size="15"
                      class="mx-50 text-muted cursor-pointer"
                      @click="contactReportedUser(data.item)"
                    />
                    <feather-icon
                      icon="Trash2Icon"
                      size="15"
                      class="mx-50 text-muted cursor-pointer"
                      @click="removeReportRow(data.item)"
                    />
                  </div>
                </template>
              </b-table>

              <AdminPaginationBar
                :page="reportPage"
                :per-page="reportPerPage"
                :total="reportTotal"
                @change="onReportPageChange"
              />
            </b-tab>
          </b-tabs>
        </b-card>
      </b-col>
    </b-row>

    <b-modal
      v-model="showTempSuspendModal"
      centered
      hide-header
      hide-footer
      modal-class="temporary-suspend-modal"
      content-class="temp-suspend-content"
    >
      <div class="temp-suspend-body">
        <h2 class="temp-suspend-title text-center">Temporary Suspend</h2>

        <b-form-group label=" " class="mb-1">
          <b-form-input v-model="tempSuspend.reason" placeholder="Write a reason" />
        </b-form-group>

        <b-form-group label="How long do you want to suspend" class="mb-2 temp-label">
          <b-form-select v-model="tempSuspend.duration" :options="suspendWeekOptions" />
        </b-form-group>

        <div class="d-flex justify-content-center">
          <b-button variant="primary" class="temp-action-btn mr-1" @click="submitTemporarySuspend">Submit</b-button>
          <b-button variant="primary" class="temp-action-btn" @click="closeTemporarySuspend">Cancel</b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BImg,
  BAlert,
  BModal,
  BRow,
  BSpinner,
  BTable,
  BTab,
  BTabs,
  BLink,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import AdminPaginationBar from '@/components/admin/AdminPaginationBar.vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import {
  debounce,
  formatAdminDate,
  normalizePagination,
  resolveMediaUrl,
  unwrapAdminResponse,
} from '@/utils/adminApiHelpers'
import {
  confirmAction,
  notifyError,
  notifyInfo,
  notifySuccess,
} from '@/utils/uiActions'

export default {
  components: {
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BImg,
    BAlert,
    BModal,
    BRow,
    BSpinner,
    BTable,
    BTab,
    BTabs,
    BLink,
    VueApexCharts,
    AdminPaginationBar,
  },
  data() {
    return {
      isLoading: false,
      loadError: '',
      isLoadingChart: false,
      isLoadingAds: false,
      isLoadingReports: false,
      isSuspending: false,
      adsSearch: '',
      reportSearch: '',
      company: {
        company_name: '',
        logo: '',
        address: '',
        pincode: '',
        city: '',
        state_of_incorporation: '',
        email: '',
        followers: 0,
        running_ads: 0,
        contact_person_name: '',
        contact: '',
        reported_users: 0,
        joining_date: '',
        account_status: '',
        is_suspended: false,
        remark: '',
      },
      reportPage: 1,
      reportPerPage: 8,
      reportTotal: 0,
      uploadedAdsPage: 1,
      uploadedAdsPerPage: 9,
      uploadedAdsTotal: 0,
      showTempSuspendModal: false,
      tempSuspend: {
        reason: '',
        duration: null,
      },
      suspendWeekOptions: [
        { value: null, text: 'Select week' },
        { value: '1 Week', text: '1 Week' },
        { value: '2 Weeks', text: '2 Weeks' },
        { value: '3 Weeks', text: '3 Weeks' },
        { value: '4 Weeks', text: '4 Weeks' },
      ],
      uploadedAds: [],
      reportFields: [
        { key: 'reportId', label: 'REPORT ID' },
        { key: 'reportedAd', label: 'REPORTED AD' },
        { key: 'reportedBy', label: 'REPORTED BY' },
        { key: 'reason', label: 'REASON' },
        { key: 'date', label: 'DATE' },
        { key: 'actions', label: 'ACTIONS' },
      ],
      reportRows: [],
      selectedPeriodDays: 4,
      periodDaysOptions: [
        { value: 4, text: '4' },
        { value: 6, text: '6' },
        { value: 8, text: '8' },
        { value: 10, text: '10' },
        { value: 12, text: '12' },
        { value: 14, text: '14' },
        { value: 16, text: '16' },
        { value: 18, text: '18' },
        { value: 20, text: '20' },
        { value: 22, text: '22' },
        { value: 24, text: '24' },
        { value: 26, text: '26' },
        { value: 28, text: '28' },
        { value: 30, text: '30' },
      ],
      legendCounts: {
        ads: 0,
        followers: 0,
        clicks: 0,
      },
      chartSeries: [
        { name: 'Ads', data: [] },
        { name: 'Followers', data: [] },
        { name: 'Clicks', data: [] },
      ],
      chartOptions: {
        chart: {
          toolbar: { show: false },
        },
        stroke: {
          width: 2,
          curve: 'straight',
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          categories: [],
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
        },
        colors: ['#5c61b3', '#e15b64', '#111827'],
        grid: {
          borderColor: '#ebe9f1',
        },
        legend: {
          show: false,
        },
      },
    }
  },
  computed: {
    advertiserId() {
      return this.$route.params.id
    },
    legendStats() {
      return {
        ads: this.formatCount(this.legendCounts.ads),
        followers: this.formatCount(this.legendCounts.followers),
        clicks: this.formatCount(this.legendCounts.clicks),
      }
    },
  },
  created() {
    this.debouncedAdsSearch = debounce(() => {
      this.uploadedAdsPage = 1
      this.fetchUploadedAds()
    }, 400)
    this.debouncedReportSearch = debounce(() => {
      this.reportPage = 1
      this.fetchAdvertiserReports()
    }, 400)
    this.fetchAdvertiser()
    this.fetchActivityChart()
    this.fetchUploadedAds()
    this.fetchAdvertiserReports()
  },
  methods: {
    onUploadedAdsPageChange(page) {
      this.uploadedAdsPage = page
      this.fetchUploadedAds()
    },
    onReportPageChange(page) {
      this.reportPage = page
      this.fetchAdvertiserReports()
    },
    formatCount(n) {
      const num = Number(n) || 0
      if (num >= 1000) return `${Math.round(num / 100) / 10}K`
      return String(num)
    },
    sumAdClicks(ads) {
      return (ads || []).reduce((sum, ad) => {
        const stats = ad.stats || {}
        return sum + Number(stats.clicks ?? stats.views ?? 0)
      }, 0)
    },
    statusBadgeVariant(status) {
      const normalized = String(status || '').toLowerCase()
      if (normalized === 'approved') return 'success'
      if (normalized === 'pending') return 'warning'
      if (normalized === 'rejected' || normalized === 'blocked') return 'danger'
      if (normalized === 'suspended') return 'secondary'
      return 'light'
    },
    adStatusVariant(status) {
      const normalized = String(status || '').toLowerCase()
      if (normalized === 'active') return 'success'
      if (normalized === 'pending') return 'warning'
      if (normalized === 'expired' || normalized === 'inactive') return 'secondary'
      if (normalized === 'rejected') return 'danger'
      return 'info'
    },
    formatAdStatusLabel(status) {
      const normalized = String(status || 'active').toLowerCase()
      if (normalized === 'active') return 'Active'
      if (normalized === 'expired') return 'Expired'
      if (normalized === 'pending') return 'Pending'
      if (normalized === 'rejected') return 'Rejected'
      if (normalized === 'inactive') return 'Inactive'
      return normalized.charAt(0).toUpperCase() + normalized.slice(1)
    },
    durationDaysFromOption(option) {
      const map = {
        '1 Week': 7,
        '2 Weeks': 14,
        '3 Weeks': 21,
        '4 Weeks': 28,
      }
      return map[option] || null
    },
    async fetchActivityChart() {
      if (!this.advertiserId) return
      this.isLoadingChart = true
      try {
        const res = await adminPanelApi.advertiserActivity(this.advertiserId, {
          period_days: Number(this.selectedPeriodDays) || 2,
        })
        const payload = unwrapAdminResponse(res)
        const labels = payload.labels || []
        const series = payload.series || {}
        const totals = payload.totals || {}

        this.chartOptions = {
          ...this.chartOptions,
          xaxis: {
            ...this.chartOptions.xaxis,
            categories: labels,
          },
        }
        this.chartSeries = [
          { name: 'Ads', data: series.ads || [] },
          { name: 'Followers', data: series.followers || [] },
          { name: 'Clicks', data: series.clicks || [] },
        ]
        this.legendCounts = {
          ads: Number(totals.ads ?? 0),
          followers: Number(totals.followers ?? 0),
          clicks: Number(totals.clicks ?? 0),
        }
      } catch (e) {
        this.chartSeries = [
          { name: 'Ads', data: [] },
          { name: 'Followers', data: [] },
          { name: 'Clicks', data: [] },
        ]
      } finally {
        this.isLoadingChart = false
      }
    },
    onPeriodDaysChange() {
      this.fetchActivityChart()
    },
    async fetchAdvertiser() {
      if (!this.advertiserId) return
      this.isLoading = true
      this.loadError = ''
      try {
        const detailRes = await adminPanelApi.advertiserDetails(this.advertiserId)
        const detail = unwrapAdminResponse(detailRes)
        this.company = {
          company_name: detail.company_name,
          logo: resolveMediaUrl(detail.logo),
          address: detail.address,
          pincode: detail.pincode,
          city: detail.city,
          state_of_incorporation: detail.state_of_incorporation || '',
          email: detail.email || '',
          followers: detail.followers ?? 0,
          running_ads: detail.running_ads ?? 0,
          contact_person_name: detail.contact_person_name,
          contact: detail.contact,
          reported_users: detail.reported_users ?? 0,
          joining_date: formatAdminDate(detail.joining_date),
          account_status: this.formatAccountStatus(
            detail.is_suspended || String(detail.status || '').toLowerCase() === 'suspended'
              ? 'suspended'
              : (detail.account_status || detail.status),
          ),
          is_suspended: Boolean(detail.is_suspended) || String(detail.status || '').toLowerCase() === 'suspended',
          remark: detail.remark || '',
        }
        if (!this.legendCounts.ads) {
          this.legendCounts.ads = Number(detail.running_ads ?? 0)
        }
        if (!this.legendCounts.followers) {
          this.legendCounts.followers = Number(detail.followers ?? 0)
        }
      } catch (e) {
        this.loadError = e.response?.data?.message || 'Unable to load advertiser details.'
      } finally {
        this.isLoading = false
      }
    },
    formatAccountStatus(status) {
      const normalized = String(status || 'pending').toLowerCase()
      if (normalized === 'approved' || normalized === 'active') return 'Approved'
      if (normalized === 'rejected') return 'Rejected'
      if (normalized === 'suspended') return 'Suspended'
      if (normalized === 'blocked') return 'Blocked'
      return 'Pending'
    },
    async fetchUploadedAds() {
      if (!this.advertiserId) return
      this.isLoadingAds = true
      try {
        const params = {
          id: this.advertiserId,
          page: this.uploadedAdsPage,
          per_page: this.uploadedAdsPerPage,
        }
        const term = this.adsSearch.trim()
        if (term) params.search = term

        const adsRes = await adminPanelApi.advertiserAds(params)
        const adsPayload = unwrapAdminResponse(adsRes)
        const ads = adsPayload.ads || []
        const pagination = normalizePagination(adsPayload, {}, adsRes)
        this.uploadedAdsTotal = pagination.total || ads.length
        this.uploadedAdsPage = pagination.currentPage || this.uploadedAdsPage
        this.uploadedAds = ads.map(ad => {
          const stats = ad.stats || {}
          return {
            id: ad.id,
            image: resolveMediaUrl(ad.image),
            status: ad.status,
            statusLabel: this.formatAdStatusLabel(ad.status),
            title: ad.title,
            timeAgo: ad.time_ago,
            statsLine: `${this.formatCount(stats.likes)} Likes · ${this.formatCount(stats.dislikes)} Dislikes · ${this.formatCount(stats.reports)} Reports`,
            engagementLine: `${this.formatCount(stats.views)} views · ${this.formatCount(stats.shares)} Share · ${this.formatCount(stats.saved)} Saved`,
          }
        })
      } catch (e) {
        this.uploadedAds = []
        this.uploadedAdsTotal = 0
      } finally {
        this.isLoadingAds = false
      }
    },
    async fetchAdvertiserReports() {
      if (!this.advertiserId) return
      this.isLoadingReports = true
      try {
        const params = {
          page: this.reportPage,
          per_page: this.reportPerPage,
          advertiser_id: this.advertiserId,
        }
        const term = this.reportSearch.trim()
        if (term) params.search = term

        const res = await adminPanelApi.reportsReportedAds(params)
        const payload = unwrapAdminResponse(res)
        const list = payload.reports || []
        const pagination = normalizePagination(payload, {}, res)
        this.reportTotal = pagination.total || list.length
        this.reportPage = pagination.currentPage || this.reportPage
        this.reportRows = list.map(r => ({
          reportId: r.report_id || r.id,
          reportedAdTitle: r.ad?.title || 'Ad',
          reportedAdLogo: resolveMediaUrl(r.ad?.image || r.company?.logo),
          reportedByName: r.reported_by?.name || '—',
          reportedByAvatar: resolveMediaUrl(r.reported_by?.profile_image),
          reason: r.reason || '',
          date: r.date || '',
        }))
      } catch (e) {
        this.reportRows = []
        this.reportTotal = 0
      } finally {
        this.isLoadingReports = false
      }
    },
    closeTemporarySuspend() {
      this.showTempSuspendModal = false
      this.tempSuspend.reason = ''
      this.tempSuspend.duration = null
    },
    async submitTemporarySuspend() {
      const reason = (this.tempSuspend.reason || '').trim()
      if (!reason) {
        notifyError(this, 'Please provide a suspension reason.')
        return
      }
      this.isSuspending = true
      try {
        const durationDays = this.durationDaysFromOption(this.tempSuspend.duration)
        await adminPanelApi.suspendAdvertiser({
          id: Number(this.advertiserId),
          reason,
          ...(durationDays ? { duration_days: durationDays } : {}),
        })
        this.closeTemporarySuspend()
        notifySuccess(this, 'Advertiser suspended temporarily.')
        await this.fetchAdvertiser()
        await this.fetchActivityChart()
      } catch (e) {
        notifyError(this, e.response?.data?.message || 'Unable to suspend advertiser.')
      } finally {
        this.isSuspending = false
      }
    },
    confirmPermanentSuspend() {
      confirmAction(
        this,
        {
          title: 'Suspend advertiser?',
          text: 'This advertiser will lose access until unsuspended.',
          confirmButtonText: 'Suspend',
        },
        async () => {
          this.isSuspending = true
          try {
            await adminPanelApi.suspendAdvertiser({
              id: Number(this.advertiserId),
              reason: 'Suspended by admin',
            })
            notifySuccess(this, 'Advertiser suspended.')
            await this.fetchAdvertiser()
            await this.fetchActivityChart()
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to suspend advertiser.')
          } finally {
            this.isSuspending = false
          }
        },
      )
    },
    unsuspendAdvertiser() {
      confirmAction(
        this,
        {
          title: 'Unsuspend advertiser?',
          text: 'Restore access for this advertiser account?',
          confirmButtonText: 'Unsuspend',
        },
        async () => {
          this.isSuspending = true
          try {
            await adminPanelApi.unsuspendAdvertiser(Number(this.advertiserId))
            notifySuccess(this, 'Advertiser unsuspended successfully.')
            await this.fetchAdvertiser()
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to unsuspend advertiser.')
          } finally {
            this.isSuspending = false
          }
        },
      )
    },
    confirmDeleteUploadedAd(ad) {
      confirmAction(
        this,
        { title: 'Delete ad?', text: `Remove "${ad.title || 'this ad'}"?`, confirmButtonText: 'Delete' },
        async () => {
          try {
            await adminPanelApi.deleteAd(ad.id)
            notifySuccess(this, 'Ad removed successfully.')
            await this.fetchUploadedAds()
            await this.fetchAdvertiser()
            await this.fetchActivityChart()
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to delete ad.')
          }
        },
      )
    },
    contactReportedUser(item) {
      notifyInfo(this, `Reporter: ${item.reportedByName}`, 'Contact')
    },
    removeReportRow(item) {
      confirmAction(
        this,
        {
          title: 'Delete report?',
          text: 'Remove this report entry?',
          confirmButtonText: 'Delete',
        },
        async () => {
          try {
            await adminPanelApi.deleteReport(item.reportId)
            notifySuccess(this, 'Report deleted successfully.')
            await this.fetchAdvertiserReports()
            await this.fetchAdvertiser()
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to delete report.')
          }
        },
      )
    },
  },
}
</script>

<style scoped lang="scss">
.left-card,
.right-card {
  border: 1px solid #ebe9f1;
}

::v-deep .custom-tabs .nav-link {
  padding: 0.45rem 0.9rem;
}

.company-logo {
  border: 1px solid #ebe9f1;
}

.details p {
  margin-bottom: 0.48rem;
  color: #5e5873;
  font-size: 0.92rem;
}

.action-row {
  gap: 0.5rem;
}

.action-btn {
  min-width: 120px;
}

.suspend-btn {
  background: #fbe4e6 !important;
  border-color: #fbe4e6 !important;
  color: #d66064 !important;
}

.temp-suspend-body {
  padding: 1.2rem 1rem 1.5rem;
}

.temp-suspend-title {
  color: #4b465c;
  font-size: 2.1rem;
  font-weight: 700;
  margin-bottom: 1.4rem;
}

.temp-label {
  color: #4b465c;
  font-size: 1rem;
}

.temp-action-btn {
  min-width: 95px;
  background-color: #001b38 !important;
  border-color: #001b38 !important;
  font-size: 1.45rem;
  line-height: 1;
  padding: 0.55rem 1.1rem;
}

::v-deep .temporary-suspend-modal .modal-content {
  border-radius: 18px;
}

.legend-wrap {
  gap: 1rem;
}

.period-control-wrap {
  gap: 0.25rem;
}

.period-days-select {
  width: 92px;
}

.legend-item {
  color: #4b465c;
  font-weight: 600;
  font-size: 0.95rem;
}

.dash {
  width: 12px;
  height: 2px;
  display: inline-block;
  margin-right: 0.4rem;
  vertical-align: middle;
}

.dash-blue {
  background: #5c61b3;
}

.dash-red {
  background: #e15b64;
}

.dash-black {
  background: #111827;
}

.followers-link {
  color: #4b465c;
  font-weight: 600;
}

.upload-controls .search-input {
  width: 185px;
}

.upload-controls .select-input {
  width: 105px;
}

.uploaded-card {
  background: #101823;
  border: none;
  border-radius: 8px;
  overflow: hidden;
}

.uploaded-image-wrap {
  position: relative;
}

.uploaded-image {
  height: 130px;
  width: 100%;
  object-fit: cover;
}

.status-chip {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 0.65rem;
}

.trash-btn {
  padding: 0.2rem 0.35rem;
}

::v-deep .report-table th {
  font-size: 0.75rem;
  color: #6e6b7b;
}

::v-deep .report-row td {
  vertical-align: middle;
}

::v-deep .page-item.active .page-link {
  background-color: #0b1f39 !important;
  border-color: #0b1f39 !important;
}

::v-deep .btn-primary,
.temp-action-btn.btn-primary {
  background-color: #4a7ff0 !important;
  border-color: #4a7ff0 !important;
}

::v-deep .select-input .vs__dropdown-toggle {
  min-height: 38px;
  border-radius: 6px;
}
</style>
